import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Import customParseFormat for parsing custom date strings
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

export const format = (date, format = 'Do MMM YYYY', fallback = null) => {
  let result = date ? dayjs(date).format(format) : null;

  if (!result || !date?.toString()?.includes(dayjs(date).year()))
    return fallback;

  return result;
};

/**
 * Attempts to parse a date string using a provided format or a set of common formats.
 *
 * @param {string} dateString - The date string to parse.
 * @param {string} [parseFormat] - (Optional) The format to use for parsing (dayjs format tokens).
 *   If not provided, tries a list of common formats including UK, US, and written forms.
 *   Examples: 'DD/MM/YYYY', 'D MMM YYYY', 'MMMM D, YYYY', etc.
 * @returns {Date|null} A JavaScript Date object if parsing succeeds, or null if parsing fails.
 *
 * Uses dayjs with customParseFormat. Falls back to native Date parsing if all formats fail.
 */
export const parseDateString = (dateString, parseFormat) => {
  let parsed = null;
  if (parseFormat) {
    parsed = dayjs(dateString, parseFormat);
  } else {
    // Try multiple formats if one is not provided
    const possibleFormats = [
      'DD-MMM-YY',
      'YYYY-MM-DD',
      'DD/MM/YYYY',
      'D/M/YYYY',
      'DD-MM-YYYY',
      'D-M-YYYY',
      'DD MMM YYYY',
      'D MMM YYYY',
      'DD MMMM YYYY',
      'D MMMM YYYY',
      'MM/DD/YYYY',
      'MMM DD, YYYY',
      'MMM D, YYYY',
      'MMMM D, YYYY',
      'MMMM DD, YYYY',
      'YYYY/MM/DD',
      'YYYY.MM.DD',
    ];

    for (let fmt of possibleFormats) {
      parsed = dayjs(dateString, fmt);
      if (parsed.isValid()) break;
    }

    // If none of the formats worked, try native Date
    if (!parsed.isValid()) {
      const nativeDate = new Date(dateString);
      if (!isNaN(nativeDate.getTime())) {
        parsed = dayjs(nativeDate);
      }
    }
  }

  return parsed.isValid() ? parsed.toDate() : null;
};

export const sessionFormat = (date, format = 'Do MMM YYYY HH:mm') =>
  date ? dayjs(date).format(format) : null;

export const isAfter = (dateA, dateB) => dayjs(dateA).isAfter(dayjs(dateB));

// return 0 - dates are equal, 1 - dateA after dateB, -1 - dateA before dateB
export const compare = (dateA, dateB, unit = null) => {
  const dayjsA = dayjs(dateA);
  const dayjsB = dayjs(dateB);
  if (dayjsA.isSame(dayjsB, unit)) {
    return 0;
  }

  return dayjsA.isAfter(dayjsB, unit) ? 1 : -1;
};

export const sub = (date, amount, unit = 'days') =>
  dayjs(date).subtract(amount, unit);

export const diff = (date1, date2, unit = 'month') =>
  dayjs(date2).diff(dayjs(date1), unit, true);

export const timeDiff = (date1, date2) => {
  const daysDiff = dayjs(date2).diff(dayjs(date1), 'day', true);
  if (daysDiff >= 365) return `${Math.floor(daysDiff / 365)} years`;
  if (daysDiff >= 30) return `${Math.floor(daysDiff / 30)} month`;
  return daysDiff > 0 ? `${Math.floor(daysDiff)} days` : '';
};
